import { MDText } from 'i18n-react';
import { getAdminApiUrl, getRegion } from '@adsk/acs-sc-web-platform-api';
import {
  SERVICE_STATUSES,
  SERVICES_PRODUCTS_MAP,
  CURRENCIES,
  TIME_ZONES,
  TRADES,
} from '@common/constants';

export const filterByActiveStatus = (collection) =>
  collection
    ? collection.filter(
        (element) =>
          element.status === SERVICE_STATUSES.ACTIVE ||
          element.status === SERVICE_STATUSES.ACTIVATING
      )
    : [];

export const mapToField = (collection, field) =>
  collection ? collection.map((element) => element[field]) : [];

export const getTimeZones = (locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));
  const map = TIME_ZONES.map((val) => ({
    value: val,
    label: T.translate(`TIME_ZONES.${val}`),
  }));

  return map;
};

export const getTrades = (locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));
  const map = TRADES.map((val) => ({
    value: val,
    label: T.translate(`TRADES.${val}`),
  }));

  return map;
};

export const getCurrencies = () =>
  CURRENCIES.map((c) => ({
    value: c,
    label: c,
  }));

export const mapProjectTypesForSelectBox = (projectTypes, locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));

  const groupBy = (array, key) =>
    array.reduce((result, currentValue) => {
      const category =
        currentValue[key] !== null
          ? currentValue[key]
          : T.translate('PROJECT_TYPE_CATEGORY_OTHER');
      // eslint-disable-next-line no-param-reassign
      (result[category] = result[category] || []).push(currentValue);

      return result;
    }, {});

  if (projectTypes.length === 0) return [];

  const categories = groupBy(projectTypes, 'category');

  return Object.keys(categories).map((key) => ({
    label: key,
    options: categories[key]
      .map((projectType) => ({
        label: projectType.displayName,
        value: projectType.value,
      }))
      .filter(({ value }) => value !== 'Template Project'),
  }));
};

const sortTemplatesByName = (templates) => {
  const clean = (name) => name.trim().toLowerCase();
  return templates.sort(({ name: name1 }, { name: name2 }) =>
    clean(name1) > clean(name2) ? 1 : -1
  );
};

export const mapTemplatesToSelectGroupOptions = (templates, locale) => {
  const T = new MDText(require(`./i18n/${locale}.json`));

  const publishedTemplates = templates.filter(({ isPublished }) => isPublished);
  const unpublishedTemplates = templates.filter(
    ({ isPublished }) => !isPublished
  );

  if (publishedTemplates.length) {
    sortTemplatesByName(publishedTemplates);
    return [
      {
        label: T.translate('PUBLISHED_TEMPLATES_TITLE'),
        options: publishedTemplates,
        subtext: T.translate('PUBLISHED_TEMPLATES_DESCRIPTION'),
      },
      {
        label: T.translate('UNPUBLISHED_TEMPLATES_TITLE'),
        options: unpublishedTemplates,
        subtext: T.translate('UNPUBLISHED_TEMPLATES_DESCRIPTION'),
      },
    ];
  }

  // We only want to group the options if there are published templates to show.
  return unpublishedTemplates;
};

// TODO: This is a workaround to get the new construction API url
// since web platform does not have a method for this yet.
// Once the method is created by web platform we should use that one and remove this.
export const getConstructionApiUrl = () => {
  return getAdminApiUrl().replace(/bim360/i, 'construction');
};

export const getProjectProductsFromServices = (projectServices) => {
  const products = SERVICES_PRODUCTS_MAP.reduce((result, map) => {
    if (!result.includes(map.product)) {
      result.push(map.product);
    }
    return result;
  }, []);
  return products.reduce((result, product) => {
    const servicesByProduct = SERVICES_PRODUCTS_MAP.filter(
      (map) => map.product === product
    ).map((e) => e.service);
    const isProductActive = servicesByProduct.some((service) =>
      projectServices.some(
        (projectService) =>
          service === projectService.serviceName &&
          (projectService.status === SERVICE_STATUSES.ACTIVE ||
            projectService.status === SERVICE_STATUSES.ACTIVATING)
      )
    );
    if (isProductActive) {
      result.push({ key: product, status: SERVICE_STATUSES.ACTIVE });
    }

    return result;
  }, []);
};

export const formatDate = (date) =>
  [
    date.getFullYear(),
    `0${date.getMonth() + 1}`.slice(-2),
    `0${date.getDate()}`.slice(-2),
  ].join('-');

export const buildCreateProjectCompanyRequest = (projectId, companyData) => [
  `${getAdminApiUrl()}/v1/projects/${projectId}/companies`,
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(companyData),
  },
];

export const buildAccountsRequest = () => {
  const region = getRegion();

  return [
    `${getAdminApiUrl()}/v1/accounts?filter[accessLevels]=accountAdmin,projectAdmin&fields=name,accessLevels,hasAcsUnifiedProjects,isAccEligible,createCompanyAccessLevels&limit=200`,
    {
      headers: { region },
    },
  ];
};

export const buildProjectCompaniesRequest = (projectId, name) =>
  `${getAdminApiUrl()}/v1/projects/${projectId}/companies?limit=200&sort=name${
    name ? `&filter[name]=${name}` : ''
  }`;

export const isEmptyObject = (object) =>
  Object.values(object).every((x) => x === null || x === '');
